import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/api';
import Icon from '../components/Icon';
import { CREATE_KINDS, TYPE_STYLES, avatarColors, initials, kindForContentType } from '../product/productUtils';
import { answerValues, answersForServer } from './onboardingUtils';

const typeChipStyle = (contentType) => {
    const colors = TYPE_STYLES[CREATE_KINDS[kindForContentType(contentType)].type];
    return { background: colors.bg, color: colors.fg, fontWeight: 600 };
};

// Strip the draft-only fields before saving
const toIcp = ({ key, status, ...icp }) => icp;

const IcpEditor = ({ icp, onSave, onCancel }) => {
    const [name, setName] = useState(icp.name);
    const [descriptor, setDescriptor] = useState(icp.descriptor);
    const [pain, setPain] = useState(icp.pains?.[0] || '');
    const [wants, setWants] = useState(icp.goals?.[0] || '');
    const save = (event) => {
        event.preventDefault();
        onSave({
            name: name.trim() || icp.name,
            descriptor: descriptor.trim(),
            pains: [pain.trim(), ...(icp.pains || []).slice(1)].filter(Boolean),
            goals: [wants.trim(), ...(icp.goals || []).slice(1)].filter(Boolean),
        });
    };
    return (
        <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input className="ls-input is-compact" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" autoFocus />
            <input className="ls-input is-compact" value={descriptor} onChange={(e) => setDescriptor(e.target.value)} aria-label="Who they are" placeholder="Who they are" />
            <label className="ls-icp-card-label" htmlFor={`pain-${icp.key}`}>Pain</label>
            <textarea id={`pain-${icp.key}`} className="ls-textarea is-compact" rows={2} value={pain} onChange={(e) => setPain(e.target.value)} />
            <label className="ls-icp-card-label" htmlFor={`wants-${icp.key}`}>Wants</label>
            <textarea id={`wants-${icp.key}`} className="ls-textarea is-compact" rows={2} value={wants} onChange={(e) => setWants(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="submit" className="ls-btn ls-btn-dark" style={{ flex: 1, fontSize: 14, padding: 9, borderRadius: 20 }}>Save</button>
                <button type="button" className="ls-btn ls-btn-secondary" onClick={onCancel}>Cancel</button>
            </div>
        </form>
    );
};

const IcpCard = ({ icp, index, pickedInQuiz, onChange, onMakePrimary }) => {
    const [isEditing, setIsEditing] = useState(false);
    const colors = avatarColors(index);
    const isKept = icp.status === 'kept';
    const isPending = icp.status === 'pending';

    if (icp.status === 'dropped') {
        return (
            <div className="ls-icp-card" style={{ flexDirection: 'row', alignItems: 'center', padding: 16, borderStyle: 'dashed', gap: 10 }}>
                <span style={{ flex: 1, fontSize: 14, color: 'var(--muted)' }}>Dropped {icp.name}</span>
                <button type="button" className="ls-link" onClick={() => onChange({ status: 'kept' })}>Undo</button>
            </div>
        );
    }

    return (
        <div className={`ls-icp-card${icp.isPrimary ? ' is-primary' : ''}${isPending ? ' is-pending' : ''}`}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div className="ls-avatar" style={{ width: 44, height: 44, fontSize: 15, background: colors.bg, color: colors.fg }}>
                    {initials(icp.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 17, fontWeight: 600 }}>{icp.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>{icp.descriptor}</div>
                </div>
                {icp.isPrimary && <span className="ls-primary-badge">PRIMARY</span>}
            </div>

            {isEditing ? (
                <IcpEditor
                    icp={icp}
                    onCancel={() => setIsEditing(false)}
                    onSave={(changes) => {
                        onChange(changes);
                        setIsEditing(false);
                    }}
                />
            ) : (
                <>
                    {icp.pains?.[0] && (
                        <div>
                            <div className="ls-icp-card-label">Pain</div>
                            <div style={{ fontSize: 14, lineHeight: 1.5 }}>{icp.pains[0]}</div>
                        </div>
                    )}
                    {icp.goals?.[0] && (
                        <div>
                            <div className="ls-icp-card-label">Wants</div>
                            <div style={{ fontSize: 14, lineHeight: 1.5 }}>{icp.goals[0]}</div>
                        </div>
                    )}
                    {(icp.contentTypes?.length > 0 || icp.channels?.length > 0) && (
                        <div>
                            <div className="ls-icp-card-label" style={{ marginBottom: 6 }}>Reach them with</div>
                            <div className="ls-chips">
                                {(icp.contentTypes || []).map(type => <span key={type} className="ls-chip" style={typeChipStyle(type)}>{type}</span>)}
                                {(icp.channels || []).map(channel => <span key={channel} className="ls-chip">{channel}</span>)}
                            </div>
                        </div>
                    )}
                    {isPending && pickedInQuiz && (
                        <div className="ls-icp-note">You didn't pick this group in the quiz. Keep as secondary?</div>
                    )}
                    <div style={{ marginTop: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            className={`ls-btn ${isKept ? 'ls-btn-dark' : 'ls-btn-outline'}`}
                            style={{ flex: 1, fontSize: 14, padding: 9, borderRadius: 20 }}
                            aria-pressed={isKept}
                            onClick={() => onChange({ status: isKept ? 'pending' : 'kept' })}
                        >
                            {isKept ? <><Icon name="check" size={16} />Kept</> : 'Keep'}
                        </button>
                        {isPending ? (
                            <button type="button" className="ls-btn ls-btn-secondary" style={{ borderRadius: 20 }} onClick={() => onChange({ status: 'dropped', isPrimary: false })}>Drop</button>
                        ) : (
                            <button type="button" className="ls-btn ls-btn-secondary" style={{ borderRadius: 20 }} onClick={() => setIsEditing(true)}>Edit</button>
                        )}
                    </div>
                    {isKept && !icp.isPrimary && (
                        <button type="button" className="ls-link" style={{ alignSelf: 'center' }} onClick={onMakePrimary}>Make primary</button>
                    )}
                </>
            )}
        </div>
    );
};

const IcpStep = ({ productId, draft, setDraft, isBusy, onBack, onFinish }) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [describe, setDescribe] = useState('');
    const [isDescribing, setIsDescribing] = useState(false);
    const [showDescribe, setShowDescribe] = useState(false);
    const icps = draft.icps || [];
    const pickedInQuiz = answerValues(draft.answers.targetCustomer).length > 0;
    const hasRequested = useRef(false);

    const setIcpList = (update) => setDraft(prev => ({ ...prev, icps: update(prev.icps || []) }));

    const generate = useCallback(async () => {
        setIsGenerating(true);
        setError('');
        try {
            const answers = answersForServer(draft.answers);
            const drafts = await api.generateIcps(productId, { answers });
            // Groups picked in the quiz are kept; other suggestions wait for Keep or Drop
            const hasPicks = answerValues(draft.answers.targetCustomer).length > 0;
            setDraft(prev => ({
                ...prev,
                icpsFrom: JSON.stringify(answers),
                icps: drafts.map((icp, index) => ({
                    ...icp,
                    key: `icp-${Date.now()}-${index}`,
                    status: !hasPicks || icp.fromQuiz || icp.isPrimary ? 'kept' : 'pending',
                })),
            }));
        } catch (e) {
            setError("Couldn't draft your ICPs. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    }, [productId, draft.answers, setDraft]);

    // Draft once on arrival; "Try again" retries after an error
    useEffect(() => {
        if (draft.icps == null && !hasRequested.current) {
            hasRequested.current = true;
            generate();
        }
    }, [draft.icps, generate]);

    const change = (key, changes) => setIcpList(list => list.map(icp => (icp.key === key ? { ...icp, ...changes } : icp)));

    const makePrimary = (key) => setIcpList(list => list.map(icp => ({ ...icp, isPrimary: icp.key === key })));

    const addDescribed = async (event) => {
        event.preventDefault();
        if (!describe.trim()) return;
        setIsDescribing(true);
        setError('');
        try {
            const [icp] = await api.generateIcps(productId, { describe: describe.trim() });
            if (!icp) throw new Error('No ICP drafted');
            setIcpList(list => [...list, { ...icp, isPrimary: list.length === 0, fromQuiz: true, key: `icp-${Date.now()}`, status: 'kept' }]);
            setDescribe('');
            setShowDescribe(false);
        } catch (e) {
            setError("Couldn't draft that ICP. Please try again.");
        } finally {
            setIsDescribing(false);
        }
    };

    const kept = icps.filter(icp => icp.status === 'kept');
    const finish = () => {
        // The primary must be one that's kept
        const primaryKept = kept.some(icp => icp.isPrimary);
        onFinish(kept.map((icp, index) => toIcp({ ...icp, isPrimary: primaryKept ? !!icp.isPrimary : index === 0 })));
    };

    return (
        <div className="ls-icp-step" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div>
                <h1 className="ls-flow-h1">Your Ideal Customer Profiles</h1>
                <div className="ls-flow-lead">Every piece of content will target one of these. You can refine them any time.</div>
            </div>

            {error && (
                <div className="ls-error" role="alert">
                    <Icon name="error" /><span style={{ flex: 1 }}>{error}</span>
                    {!draft.icps && <button type="button" className="ls-btn ls-btn-secondary" onClick={generate}>Try again</button>}
                </div>
            )}

            {isGenerating ? (
                <div className="ls-icp-cards" aria-busy="true">
                    {[0, 1, 2].map(i => (
                        <div key={i} className="ls-icp-card" style={{ minHeight: 320 }}>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <div className="ls-skeleton" style={{ width: 44, height: 44, borderRadius: '50%' }} />
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <div className="ls-skeleton" style={{ height: 16, width: '70%' }} />
                                    <div className="ls-skeleton" style={{ height: 12, width: '50%' }} />
                                </div>
                            </div>
                            <div className="ls-skeleton" style={{ height: 48 }} />
                            <div className="ls-skeleton" style={{ height: 48 }} />
                            {i === 0 && <div style={{ fontSize: 13, color: 'var(--muted)' }}>Drafting ICPs from your sources and answers…</div>}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="ls-icp-cards">
                    {icps.map((icp, index) => (
                        <IcpCard
                            key={icp.key}
                            icp={icp}
                            index={index}
                            pickedInQuiz={pickedInQuiz}
                            onChange={(changes) => change(icp.key, changes)}
                            onMakePrimary={() => makePrimary(icp.key)}
                        />
                    ))}
                </div>
            )}

            {showDescribe && (
                <form onSubmit={addDescribed} className="ls-card" style={{ maxWidth: 640, gap: 10 }}>
                    <label className="ls-card-title" htmlFor="describe-icp">Describe another ICP</label>
                    <textarea
                        id="describe-icp"
                        className="ls-textarea"
                        rows={2}
                        autoFocus
                        value={describe}
                        onChange={(e) => setDescribe(e.target.value)}
                        placeholder="e.g. Parents who cook one meal for the whole family"
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="submit" className="ls-btn ls-btn-dark" style={{ fontSize: 14, padding: '9px 18px' }} disabled={isDescribing || !describe.trim()}>
                            {isDescribing ? 'Drafting…' : 'Draft ICP'}
                        </button>
                        <button type="button" className="ls-btn ls-btn-secondary" onClick={() => setShowDescribe(false)}>Cancel</button>
                    </div>
                </form>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 'auto' }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <button type="button" className="ls-btn ls-btn-ghost" onClick={onBack} disabled={isBusy}>Back</button>
                    {!showDescribe && (
                        <button type="button" className="ls-link" style={{ fontSize: 14 }} onClick={() => setShowDescribe(true)} disabled={isGenerating}>
                            + Describe another ICP
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    className="ls-btn ls-btn-primary is-large"
                    onClick={finish}
                    disabled={kept.length === 0 || isBusy || isGenerating}
                    title={kept.length ? '' : 'Keep at least one ICP'}
                >
                    {isBusy ? 'Saving…' : 'Write my first posts'}
                </button>
            </div>
        </div>
    );
};

export default IcpStep;
